/* The one table.
 *
 * The spec says invest in a single excellent table used on 40+ screens, and
 * this is it. It is a CSS grid whose column template is passed in by the caller,
 * which means every list in the app shares one row rhythm — and a screen that
 * wants a different rhythm has to write the template down, where it can be
 * reviewed, rather than nudging padding.
 *
 * `cols` is a `grid-template-columns` value. `minWidth` is the width below which
 * the wrapper scrolls horizontally instead of crushing the columns; on a laptop
 * this is what keeps a 10-column table readable.
 */

import type { ReactNode } from 'react';
import css from './ui.module.css';

export function Table({
  cols,
  head,
  minWidth = 900,
  children,
  sticky,
}: {
  cols: string;
  head: ReactNode[];
  minWidth?: number;
  children: ReactNode;
  sticky?: boolean;
}) {
  return (
    <div className={css.tableWrap}>
      <div style={{ minWidth }}>
        <div
          className={`${css.th} ${sticky ? css.thSticky : ''}`}
          style={{ gridTemplateColumns: cols }}
          role="row"
        >
          {head.map((h, i) => (
            <span key={i}>{h}</span>
          ))}
        </div>
        {children}
      </div>
    </div>
  );
}

export function TRow({
  cols,
  children,
  onClick,
  selected,
}: {
  cols: string;
  children: ReactNode;
  onClick?: () => void;
  selected?: boolean;
}) {
  const style = { gridTemplateColumns: cols };
  if (onClick) {
    return (
      <button
        type="button"
        className={`${css.tr} ${css.trBtn} ${selected ? css.trSel : ''}`}
        style={style}
        onClick={onClick}
      >
        {children}
      </button>
    );
  }
  return (
    <div className={`${css.tr} ${selected ? css.trSel : ''}`} style={style}>
      {children}
    </div>
  );
}

/** A two-line cell: the fact, and the qualifier under it. The prototype uses
 *  this everywhere, because a name alone is not enough to act on. */
export function CellStack({ top, sub }: { top: ReactNode; sub?: ReactNode }) {
  return (
    <span className={css.cellStack}>
      <span>{top}</span>
      {sub ? <span className={css.sub}>{sub}</span> : null}
    </span>
  );
}

export function CellRow({ children }: { children: ReactNode }) {
  return <span className={css.cellRow}>{children}</span>;
}

export const COL = {
  /** A reference id. A button, because it opens a drawer rather than a page. */
  ref: (children: ReactNode, onClick?: () => void) =>
    onClick ? (
      <button type="button" className={css.ref} onClick={onClick}>
        {children}
      </button>
    ) : (
      <span className={css.monoStrong}>{children}</span>
    ),
  right: (children: ReactNode) => <span className={css.num}>{children}</span>,
  label: (children: ReactNode) => <span className={css.label}>{children}</span>,
} as const;

export { css as uiCss };
